import React, { useEffect, useState } from "react";
import { apiClient, formatApiError } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { useAuth } from "../../lib/auth";
import {
  Database, Download, Trash2, Loader2, RefreshCw, ShieldCheck, Info,
  Calendar, CheckCircle2, XCircle, CloudUpload, HardDrive, AlertTriangle, RotateCcw,
} from "lucide-react";

/**
 * BackupsTab v2 — full production dashboard for the CARTAL backup system.
 * Status panel + archive list + manual run + per-row download / validate /
 * delete / restore. Restore requires super_admin and a typed confirmation.
 */
export default function BackupsTab() {
  const { lang } = useI18n();
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState(null);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoreConfirm, setRestoreConfirm] = useState("");
  const [restoring, setRestoring] = useState(false);

  const reload = async () => {
    setBusy(true);
    try {
      const [list, st] = await Promise.all([
        apiClient.get("/admin/backups"),
        apiClient.get("/admin/backups/status"),
      ]);
      setItems(list.data.items || []);
      setStatus(st.data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const runNow = async () => {
    setRunning(true); setErr(null);
    try {
      await apiClient.post("/admin/backups/run");
      await reload();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setRunning(false); }
  };

  const remove = async (name) => {
    if (!window.confirm(lang === "ar" ? `حذف ${name}؟` : `Delete ${name}?`)) return;
    try {
      await apiClient.delete(`/admin/backups/${encodeURIComponent(name)}`);
      await reload();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const download = (name) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/backups/download/${encodeURIComponent(name)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const askRestore = (name) => {
    setRestoreTarget(name);
    setRestoreConfirm("");
  };

  const doRestore = async () => {
    if (!restoreTarget || restoreConfirm !== "RESTORE") return;
    setRestoring(true); setErr(null);
    try {
      await apiClient.post(`/admin/backups/restore/${encodeURIComponent(restoreTarget)}`);
      setRestoreTarget(null); setRestoreConfirm("");
      await reload();
      alert(lang === "ar" ? "تمت الاستعادة بنجاح. تم إنشاء نسخة أمان قبل الاستعادة." : "Restore complete. A safety backup was created first.");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setRestoring(false); }
  };

  return (
    <div className="space-y-6" data-testid="backups-tab">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl text-[#0a0a0a] inline-flex items-center gap-2">
            <Database className="w-6 h-6 text-[#8a6f2e]" />
            {lang === "ar" ? "النسخ الاحتياطي والاستعادة" : "Backups & Restore"}
          </h2>
          <p className="text-sm text-[#6b6b6b] mt-1">
            {lang === "ar"
              ? "نسخ تلقائية يومية 3:00 ص (الرياض)، مع رفع خارجي اختياري واستعادة كاملة."
              : "Daily auto-backup at 03:00 Riyadh time, optional remote upload, full restore."}
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={reload} disabled={busy} className="btn-ghost text-sm" data-testid="backup-refresh">
            <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> {lang === "ar" ? "تحديث" : "Refresh"}
          </button>
          <button onClick={runNow} disabled={running} className="btn-primary text-sm" data-testid="backup-run-now">
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            {lang === "ar" ? "نسخ احتياطي الآن" : "Run backup now"}
          </button>
        </div>
      </div>

      {/* Status dashboard */}
      {status && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="backup-status-grid">
          <StatusCard
            icon={HardDrive}
            label={lang === "ar" ? "عدد النسخ" : "Archives"}
            value={status.archives_count}
            sub={`${status.total_size_mb} MB ${lang === "ar" ? "إجمالي" : "total"}`}
            color="#0a4f3a"
          />
          <StatusCard
            icon={CheckCircle2}
            label={lang === "ar" ? "آخر نجاح" : "Last success"}
            value={status.last_success ? new Date(status.last_success.ts).toLocaleDateString(lang === "ar" ? "ar-SA" : "en-GB") : "—"}
            sub={status.last_success ? `${status.last_success.size_mb} MB · ${status.last_success.reason}` : (lang === "ar" ? "لا يوجد" : "None")}
            color="#1f6f3a"
          />
          <StatusCard
            icon={status.last_failure ? XCircle : CheckCircle2}
            label={lang === "ar" ? "آخر فشل" : "Last failure"}
            value={status.last_failure ? new Date(status.last_failure.ts).toLocaleDateString(lang === "ar" ? "ar-SA" : "en-GB") : (lang === "ar" ? "لا يوجد" : "None")}
            sub={status.last_failure?.error?.slice(0, 60) || "—"}
            color={status.last_failure ? "#dc2626" : "#1f6f3a"}
            failureCount={status.failures_count}
          />
          <StatusCard
            icon={CloudUpload}
            label={lang === "ar" ? "التخزين الخارجي" : "Remote storage"}
            value={
              status.remote.skipped
                ? (lang === "ar" ? "اختياري" : "Optional")
                : status.remote.reachable
                  ? (lang === "ar" ? "متصل" : "Connected")
                  : (lang === "ar" ? "خطأ" : "Error")
            }
            sub={
              status.remote.skipped
                ? (lang === "ar" ? "نسخ محلية فقط — أضف مفاتيح R2/S3 للتفعيل" : "Local-only — add R2/S3 keys to enable")
                : `${status.remote.provider || ""} ${status.remote.bucket || ""}`
            }
            color={status.remote.skipped ? "#0a4f3a" : status.remote.reachable ? "#1f4f8a" : "#dc2626"}
          />
        </div>
      )}

      <div className="flex items-start gap-2 rounded-xl p-3" style={{ backgroundColor: "#fafaf7", border: "1px solid rgba(201,165,92,0.30)" }}>
        <Info className="w-4 h-4 text-[#8a6f2e] mt-0.5 shrink-0" />
        <p className="text-xs text-[#525252] leading-relaxed">
          {lang === "ar"
            ? "النسخ تُحفظ على القرص في /app/backups وتدوير يومي تلقائي. لتفعيل الرفع الخارجي إلى Cloudflare R2 (مُوصى به) أو AWS S3، اضبط المفاتيح: BACKUP_REMOTE_ENABLED=1, BACKUP_REMOTE_BUCKET, BACKUP_REMOTE_ENDPOINT (لـ R2)، BACKUP_REMOTE_ACCESS_KEY, BACKUP_REMOTE_SECRET_KEY."
            : "Archives are stored locally at /app/backups with daily rotation. To enable remote upload to Cloudflare R2 (recommended) or AWS S3, set: BACKUP_REMOTE_ENABLED=1, BACKUP_REMOTE_BUCKET, BACKUP_REMOTE_ENDPOINT (for R2), BACKUP_REMOTE_ACCESS_KEY, BACKUP_REMOTE_SECRET_KEY."}
        </p>
      </div>

      {err && (
        <div className="rounded-xl p-3 text-sm text-red-700 border border-red-200 bg-red-50">{String(err)}</div>
      )}

      {/* Archives table */}
      <div className="surface rounded-2xl overflow-hidden">
        <table className="w-full text-sm" data-testid="backup-table">
          <thead className="bg-[#0a0a0a]/[0.04]">
            <tr>
              <th className="text-start font-semibold text-[#3a3a3a] px-4 py-3">{lang === "ar" ? "الاسم" : "Name"}</th>
              <th className="text-start font-semibold text-[#3a3a3a] px-4 py-3">{lang === "ar" ? "الحجم" : "Size"}</th>
              <th className="text-start font-semibold text-[#3a3a3a] px-4 py-3">{lang === "ar" ? "التاريخ" : "Date"}</th>
              <th className="text-end font-semibold text-[#3a3a3a] px-4 py-3">{lang === "ar" ? "إجراءات" : "Actions"}</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={4} className="text-center text-[#6b6b6b] py-10">
                {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : (lang === "ar" ? "لا توجد نسخ بعد." : "No backups yet.")}
              </td></tr>
            ) : items.map((b) => (
              <tr key={b.name} className="border-t border-[#0a0a0a]/5 hover:bg-[#fafaf7]" data-testid={`backup-row-${b.name}`}>
                <td className="px-4 py-3 font-mono text-[12px]">{b.name}</td>
                <td className="px-4 py-3 text-[#525252]">{b.size_mb} MB</td>
                <td className="px-4 py-3 text-[#525252] inline-flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5 text-[#8a6f2e]" />
                  {new Date(b.created_at).toLocaleString(lang === "ar" ? "ar-SA" : "en-GB")}
                </td>
                <td className="px-4 py-3 text-end">
                  <div className="inline-flex flex-wrap gap-2 justify-end">
                    <button onClick={() => download(b.name)} className="btn-ghost text-xs" data-testid={`backup-download-${b.name}`}>
                      <Download className="w-3.5 h-3.5" /> {lang === "ar" ? "تحميل" : "Download"}
                    </button>
                    {isSuper && (
                      <button
                        onClick={() => askRestore(b.name)}
                        className="btn-ghost text-xs text-[#1f4f8a] border-[#1f4f8a]/35 hover:bg-[#1f4f8a]/5"
                        data-testid={`backup-restore-${b.name}`}
                      >
                        <RotateCcw className="w-3.5 h-3.5" /> {lang === "ar" ? "استعادة" : "Restore"}
                      </button>
                    )}
                    <button onClick={() => remove(b.name)} className="btn-ghost text-xs text-red-700 border-red-200 hover:bg-red-50" data-testid={`backup-delete-${b.name}`}>
                      <Trash2 className="w-3.5 h-3.5" /> {lang === "ar" ? "حذف" : "Delete"}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Restore confirm modal */}
      {restoreTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(10,10,10,0.7)" }}
          data-testid="restore-modal"
        >
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl">
            <div className="flex items-center gap-2 mb-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              <h3 className="font-display text-xl font-semibold">
                {lang === "ar" ? "تأكيد الاستعادة" : "Confirm Restore"}
              </h3>
            </div>
            <p className="text-sm text-[#3a3a3a] leading-relaxed mb-2">
              {lang === "ar"
                ? <>سيتم استعادة قاعدة البيانات وجميع الملفات من <code className="bg-[#fafaf7] px-1 rounded text-[11px]">{restoreTarget}</code>. سيتم استبدال البيانات الحالية بالكامل.</>
                : <>The database and all files will be restored from <code className="bg-[#fafaf7] px-1 rounded text-[11px]">{restoreTarget}</code>. Current data will be completely replaced.</>}
            </p>
            <p className="text-xs text-[#1f6f3a] mb-4">
              {lang === "ar" ? "✓ سنُنشئ نسخة أمان تلقائية قبل الاستعادة." : "✓ A safety backup will be created automatically first."}
            </p>
            <label className="block text-xs text-[#6b6b6b] mb-1">
              {lang === "ar" ? "اكتب RESTORE للتأكيد:" : "Type RESTORE to confirm:"}
            </label>
            <input
              type="text"
              value={restoreConfirm}
              onChange={(e) => setRestoreConfirm(e.target.value)}
              className="input-dark"
              autoFocus
              data-testid="restore-confirm-input"
            />
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => { setRestoreTarget(null); setRestoreConfirm(""); }} className="btn-ghost text-sm" data-testid="restore-cancel">
                {lang === "ar" ? "إلغاء" : "Cancel"}
              </button>
              <button
                onClick={doRestore}
                disabled={restoring || restoreConfirm !== "RESTORE"}
                className="btn-primary text-sm bg-red-700 hover:bg-red-800 disabled:opacity-50"
                style={{ backgroundColor: restoreConfirm === "RESTORE" ? "#b91c1c" : undefined }}
                data-testid="restore-confirm"
              >
                {restoring ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                {lang === "ar" ? "تنفيذ الاستعادة" : "Run restore"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusCard({ icon: Icon, label, value, sub, color, failureCount }) {
  return (
    <div
      className="rounded-2xl p-4 border-2 bg-white"
      style={{ borderColor: `${color}26` }}
    >
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-widest font-semibold text-[#6b6b6b]">{label}</span>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <div className="font-display text-xl text-[#0a0a0a] mt-2 truncate">{value}</div>
      {failureCount > 0 && (
        <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700">
          {failureCount}× 24h
        </span>
      )}
      {sub && <div className="text-[11px] text-[#8a8a8a] mt-1 truncate">{sub}</div>}
    </div>
  );
}
